import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, FormHelperText, Typography } from "@mui/material";

import { StyledTextField } from "../../../Components/EditVehicleDetails/EditDialogShared";
import {
    CancelFormActionButton,
    PrimaryFormLoadingActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import MovementBuilder from "./MovementBuilder";
import WorkOrderPreviewPanel from "./WorkOrderPreviewPanel";
import {
    FieldLabel,
    FORM_TOKENS,
    FormBand,
    FormCard,
    Helper,
} from "./WorkOrderFormShared";
import { useCurrentUserID, useWorkOrderConfig } from "./hooks/useWorkOrderConfig";
import {
    ENTRY_MODE,
    blockedActionNotice,
    errorsAfterMovementChange,
    isMovementDateEditable,
    defaultMovementDateTime,
    buildPreviewPayload,
    buildWorkOrderPayload,
    movementDateTimeBounds,
    movementPasteSpec,
    singleValuesFromParsed,
    parsePastedRows,
    seedSingleValues,
    pasteValidationError,
    removeSerialsFromText,
    resolveFormSerials,
    initialEntryMode,
    itemsToPasteText,
    itemToRowValues,
    resolveWorkOrderTypeCode,
    resolveWorkOrderTypeLabel,
    serialCheckState,
    serialSignature,
    routeSignature,
    serialsToText,
    singleEntryColumns,
    singleEntryErrors,
    summariseSerialReport,
    validateSerialCheck,
    validateWorkOrderForm,
    workOrderFormAccess,
    formatWorkOrderDateTime,
} from "./workOrderHelpers";
import {
    checkWorkOrderSerials,
    createWorkOrder,
    updateWorkOrder,
} from "../../../Api/WorkOrderApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import { usePermissions } from "../../../Context/PermissionContext";
import { getMetricTimeStamp } from "../../../Helper/DatePicker/DateConverter";
import useWorkOrderEntities from "./hooks/useWorkOrderEntities";

// The whole of raising a work order, or editing one you raised that is still
// pending: the state, the validation, the dry run and the submit.
//
// ONE MOVEMENT, and no control that says so. The movements array, its
// numbering and its add/remove scaffolding are gone -- a work order carries
// one movement and as many serials as can be pasted into it. The payload is
// untouched: buildWorkOrderPayload still sends `movements: [oneMovement]`.
//
// NO WORK ORDER TYPE CONTROL either. Every work order raised from this module
// is an asset movement, and a required dropdown with one possible answer is a
// question the form already knows the answer to. The FIELD is still sent and
// still gates permissions per type -- resolveWorkOrderTypeCode picks it out of
// the config list the user is allowed to raise, and workOrderFormAccess still
// shows the denial state to anyone who holds none.
//
// NO REQUEST SECTION. It existed to hold the type and the remarks, and the
// type has just gone. Remarks moves to the last band, after the thing it is
// about.
//
// Config drives the rest: the movement types and each one's allowed source and
// destination entity types. The create service enforces the same lists the
// pickers are narrowed by, so the form cannot offer a choice the write will
// refuse.
//
// Sends NO documentIDs. Document upload is deferred and CreateWorkOrderSchema
// is unknown = RAISE, so the key would be a 400 rather than an ignored field.

const emptyMovement = (
    serialText = "",
    entryMode = ENTRY_MODE.SINGLE,
    movementTypeCode = ""
) => ({
    // SINGLE by default: most work orders move one asset, and the paste box
    // was the only way in for far too long.
    entryMode,
    // Single mode's fields. The box is DERIVED from these -- see patchSingle
    // in MovementBuilder -- so there is one payload path, not two.
    //
    // SEEDED FROM THE SAME TEXT the box is, and not left empty -- see
    // seedSingleValues. movementFromDetail already does this on the EDIT path
    // (itemToRowValues); this is the create path doing the same thing.
    singleValues: seedSingleValues(serialText, entryMode),
    // CHOSEN BEFORE THE PAGE when the FAB's tree asked for it. Empty
    // otherwise, which is every other way in -- a deep link, a refresh, an
    // edit -- and the dropdown then opens unchosen rather than on a type
    // nobody picked.
    movementTypeCode,
    // NOW, in the org's timezone. The field is read-only on a create and
    // stamped again at submit -- see isMovementDateEditable and
    // stampMovementDate -- so this seed is what the maker SEES rather than
    // what is finally sent.
    movementDateTime: defaultMovementDateTime(),
    destinationEntityTypeCode: "",
    destinationEntityID: null,
    sourceEntityID: null,
    // The box, as typed. `serials` is DERIVED from it -- see
    // resolveMovementSerials -- and is what the payload builder and the
    // validator still take, unchanged.
    serialText,
    itemOverrides: {},
});

const emptyForm = (
    serialText = "",
    entryMode = ENTRY_MODE.SINGLE,
    movementTypeCode = ""
) => ({
    remarks: "",
    movements: [emptyMovement(serialText, entryMode, movementTypeCode)],
});

// A stored movement -> the shape this form edits.
//
// movementDateTime comes back as an ISO string in UTC and the control wants
// the user's own wall clock, so it goes through the org-timezone helper -- the
// same one every other date on this page uses. Never `new Date(iso)`, which
// would use the BROWSER's zone and show an edit form hours off the value that
// is actually stored.
const movementFromDetail = (movement, columns = SEED_FALLBACK_COLUMNS) => {
    const items = (movement?.items || []).filter((item) => item?.serialNumber);
    return {
    // An existing request opens in the mode that describes what it holds: one
    // item is a single asset, several are a paste. Showing one field over a
    // request carrying twelve serials would misrepresent what is about to be
    // saved.
    entryMode: initialEntryMode(items.length),
    // Seeded with the columns KNOWN AT THIS MOMENT, which on the first pass
    // is the bare serial: this runs in a useState initialiser, before the
    // movement type config has arrived, and the column list depends on the
    // movement type. The re-seed effect below fills in the rest the moment
    // the config lands.
    singleValues: itemToRowValues(items[0], columns),
    movementTypeCode: movement?.movementTypeCode || "",
    movementDateTime: movement?.movementDateTime
        ? getMetricTimeStamp(movement.movementDateTime, "YYYY-MM-DD[T]HH:mm")
        : "",
    destinationEntityTypeCode: movement?.destinationEntityTypeCode || "",
    destinationEntityID: movement?.destinationEntityID ?? null,
    // Per-item on the wire, per-movement in this form. Seeded from the first
    // item, which is what it was fanned out from on the way in.
    sourceEntityID: movement?.items?.[0]?.sourceEntityID ?? null,
    serialText: itemsToPasteText(items, columns),
    itemOverrides: {},
    };
};

// initialSerials is the Assets tab's selection, handed over in router state.
// It only ever seeds a NEW request: an edit already has its own serials, and
// pasting a selection over them would silently rewrite somebody's request.
// What movementFromDetail can know before the config arrives. Re-derived
// properly by the re-seed effect.
const SEED_FALLBACK_COLUMNS = singleEntryColumns(null);

// How long the form waits after the last change before it checks.
//
// Long enough that typing a serial by hand is one request rather than one per
// character, short enough that it reads as the form keeping up rather than as
// a delay. A paste arrives as a single change and does not wait for the whole
// window to be useful -- it is the last change either way.
const AUTO_CHECK_DELAY_MS = 600;

const formFromWorkOrder = (
    workOrder,
    initialSerials = [],
    columns = SEED_FALLBACK_COLUMNS,
    // Both decided before this page opened -- see the entryMode and
    // initialMovementTypeCode props.
    entryMode = ENTRY_MODE.SINGLE,
    movementTypeCode = ""
) => {
    if (!workOrder?.id) {
        return emptyForm(serialsToText(initialSerials), entryMode, movementTypeCode);
    }
    return {
        remarks: workOrder.remarks || "",
        movements: (workOrder.movements || []).length
            ? [movementFromDetail(workOrder.movements[0], columns)]
            : [emptyMovement()],
    };
};

const WorkOrderForm = ({
    workOrder = null,
    onCancel,
    onSaved,
    onSavingChange,
    // The page owns the heading and this component owns the config the type
    // is resolved from, so the label is reported UP -- the same shape
    // onSavingChange already uses.
    onWorkOrderTypeLabelChange,
    // From the Assets tab, via location state. Absent on a direct visit, a
    // refresh and the module's floating button, all three of which are a blank
    // form rather than a broken one.
    initialSerials = [],
    // Where onCancel actually goes, resolved by the page. The denial state's
    // only control says it out loud, and it must not claim "Work Orders" while
    // returning a user to the Assets tab they came from.
    backLabel = "Work Orders",
    // The page's own pageMeta. Read for ONE thing: the customer entity type
    // definition behind the destination picker's "+ New Customer" option. An
    // absent one hides the option rather than opening a dialog with no fields.
    metaInfo = null,
    // ONE ASSET OR SEVERAL, decided BEFORE this page opened -- by the floating
    // button's two options, or by how many rows were ticked on the Assets tab.
    // The form does not ask and has no toggle on it: the question is answered
    // once, at the moment the flow starts, and a page that asks again is
    // asking someone to repeat themselves.
    entryMode = ENTRY_MODE.SINGLE,
    // The movement the FAB's tree already asked for. The form still shows the
    // dropdown and it is still editable: the tree answers the question, it
    // does not remove it.
    initialMovementTypeCode = "",
}) => {
    const isEdit = Boolean(workOrder?.id);
    const { openNotification } = useToast();
    const { hasPermission } = usePermissions();
    // Needed for the EDIT gate: the backend refuses an edit of somebody else's
    // request, so the form has to know whose it is before it opens one.
    const { currentUserID, isResolved: userResolved } = useCurrentUserID();

    // BOTH config tables: the movement dropdown needs movementType, and its
    // two JSON columns are what narrow the entity pickers.
    const {
        workOrderTypes,
        movementTypes,
        movementTypesByCode,
        isLoading: configLoading,
        errorMessage: configError,
    } = useWorkOrderConfig();

    const [form, setForm] = useState(() =>
        formFromWorkOrder(
            workOrder,
            initialSerials,
            SEED_FALLBACK_COLUMNS,
            entryMode,
            initialMovementTypeCode
        )
    );

    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // --- the dry run -------------------------------------------------------
    // `checkedSerials` is the list the last check actually RAN AGAINST. It is
    // kept beside the report rather than derived from it because the report
    // says nothing about what was left out, and because a FAILED check (a 422)
    // is still a real answer about a real list.
    const [serialReport, setSerialReport] = useState(null);
    const [checkedSerials, setCheckedSerials] = useState(null);
    // The ROUTE the last check ran against, beside the serials and for the
    // same reason: the verdicts describe both. See routeSignature.
    const [checkedRoute, setCheckedRoute] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    // Did the LAST attempt fail at the transport? The automatic check below
    // must not retry a dead server on a loop, so a failure parks it until an
    // input changes or the user asks again.
    const [checkFailed, setCheckFailed] = useState(false);

    // The two are ONE FACT -- what the answer on screen describes -- so they
    // are written through one pair of functions rather than side by side at
    // each of the four call sites. Set one and forget the other and the
    // report silently reads as current against a route it never saw.
    const clearCheck = useCallback(() => {
        setSerialReport(null);
        setCheckedSerials(null);
        setCheckedRoute(null);
    }, []);

    // The page owns a Back button and a breadcrumb, and neither may be used
    // mid-write: the request is already in flight, so leaving would hide the
    // serial report that is the only diagnosis of a failure.
    useEffect(() => {
        onSavingChange?.(saving);
    }, [saving, onSavingChange]);

    // Re-seeded when the record IDENTITY changes, not on every render of the
    // same one. Keyed on the id rather than on the object: the page refetches
    // the detail and a new object reference for the same work order must not
    // throw away what the user has typed since.
    const seededID = useRef(workOrder?.id ?? null);
    useEffect(() => {
        const nextID = workOrder?.id ?? null;
        if (seededID.current === nextID) return;
        seededID.current = nextID;
        // A different record means the config-aware re-seed below has to run
        // again for it.
        reseededID.current = null;
        setErrors({});
        setMovementTouched(false);
        clearCheck();
        setForm(
            formFromWorkOrder(
                workOrder,
                initialSerials,
                SEED_FALLBACK_COLUMNS,
                entryMode,
                initialMovementTypeCode
            )
        );
        // initialSerials is intentionally not a dependency: it is a seed taken
        // once, and a fresh array literal from the router on every render
        // would reset the form under the user's hands.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workOrder]);

    // THE SECOND HALF OF SEEDING AN EDIT, and it cannot be folded into the
    // first.
    //
    // The box's shape depends on the movement type -- a Delivery row is a
    // serial and a loan id -- and the movement type config is fetched. The
    // seed above runs in a useState initialiser, before that fetch resolves,
    // so it can only write serial numbers. This fills in the rest once the
    // config is in hand.
    //
    // ONCE PER RECORD, guarded by its own ref, and not on every render where
    // the config happens to be present. Re-running it would overwrite whatever
    // the maker had typed since -- which is the exact failure mode the
    // identity guard above exists to prevent, and it must not be reintroduced
    // through the back door. In practice the form is behind a spinner until
    // the config arrives, so nothing has been typed yet; the guard is what
    // makes that a guarantee rather than a timing accident.
    const reseededID = useRef(null);
    useEffect(() => {
        const detail = workOrder?.movements?.[0];
        if (!workOrder?.id || !detail) return;
        if (reseededID.current === workOrder.id) return;

        const movementType = movementTypesByCode?.[detail.movementTypeCode];
        // Not an error -- the config simply has not arrived. Try again on the
        // render that brings it.
        if (!movementType) return;

        const columns = singleEntryColumns(movementType);
        // Nothing beyond the serial to restore, so nothing to rewrite.
        if (columns.length <= 1) {
            reseededID.current = workOrder.id;
            return;
        }

        reseededID.current = workOrder.id;
        setForm((prev) => ({
            ...prev,
            movements: [
                {
                    ...prev.movements[0],
                    ...movementFromDetail(detail, columns),
                },
            ],
        }));
    }, [workOrder, movementTypesByCode]);

    const movement = form.movements[0];

    // The pickers' options, fetched per entity type and only for the types the
    // chosen movement type actually allows.
    const { entityState, addEntity } = useWorkOrderEntities(
        form.movements,
        movementTypesByCode
    );

    // Resolved ONCE per mount, from the org's today. Recomputing it on every
    // render would let the window slide under a form left open over midnight,
    // and re-validate a date that was legal when it was typed.
    //
    // An EDIT keeps the full window whatever menu item opened the create page:
    // the stored date is what it is, and narrowing the window under a saved
    // value would refuse a request for a date the user never touched.
    const dateBounds = useMemo(() => movementDateTimeBounds(), []);

    // See isMovementDateEditable: a create records a movement happening now,
    // so its date is the clock's answer rather than a field.
    const dateIsEditable = useMemo(
        () => isMovementDateEditable({ isEdit }),
        [isEdit]
    );

    // THE CLOCK WINS on a locked date. The form seeds "now" at mount, and a
    // form left open for an hour would otherwise record the hour it was
    // opened in -- a date nobody chose and nobody would notice was wrong.
    // Applied to the payload only: re-stamping form state would rewrite the
    // field under the user on every keystroke elsewhere.
    const stampMovementDate = useCallback(
        (candidate) => {
            if (dateIsEditable) return candidate;
            return {
                ...candidate,
                movements: (candidate.movements || []).map((movement) => ({
                    ...movement,
                    movementDateTime: defaultMovementDateTime(),
                })),
            };
        },
        [dateIsEditable]
    );

    // EVERY movement edit goes through here, so this is the one place that
    // knows a refusal has been acted on. See errorsAfterMovementChange: an
    // error the user has just fixed says the fix did not work.
    //
    // errorsAfterMovementChange returns the SAME object when nothing went
    // stale, so the identity check below is the whole of "did this edit touch
    // a refusal" -- an unrelated keystroke sets no state here at all.
    const currentMovement = form.movements[0];
    // The form filling ITSELF in -- a single allowed destination type resolved
    // for the maker. Writes the movement without claiming a touch, so an edit
    // that merely resolved its own dropdown does not release the automatic
    // check.
    const autoFillMovement = useCallback((next) => {
        setForm((prev) => ({ ...prev, movements: [next] }));
    }, []);
    // Has the maker changed the movement since the form was seeded? Only an
    // edit reads it -- see editUntouched.
    const [movementTouched, setMovementTouched] = useState(false);
    const updateMovement = useCallback(
        (next) => {
            setMovementTouched(true);
            setForm((prev) => ({ ...prev, movements: [next] }));
            const remaining = errorsAfterMovementChange(errors, currentMovement, next);
            if (remaining === errors) return;
            setErrors(remaining);
            // The notice is RE-DERIVED from what is left, not cleared: fixing
            // the field it named should reveal the next blocker rather than
            // pretend there is none.
            setBlockedNotice((notice) =>
                notice ? blockedActionNotice(remaining) : notice
            );
        },
        [errors, currentMovement]
    );

    // Why the last Preview or Submit did nothing, shown AT the button that did
    // it. Cleared the moment either runs cleanly.
    const [blockedNotice, setBlockedNotice] = useState(null);

    // Refuse, say why at the action, and TAKE THE USER TO THE FIELD.
    //
    // The message alone is not enough when the field is four hundred pixels
    // away: the sentence explains the refusal, the scroll shows what to do
    // about it. Focus follows the scroll so a keyboard user lands there too.
    const refuseAction = useCallback((nextErrors) => {
        setErrors(nextErrors);
        const notice = blockedActionNotice(nextErrors);
        setBlockedNotice(notice);
        if (!notice?.field) return;
        // A purpose-built hook, not the test id. Read after paint so the
        // field's error state is on screen by the time it is scrolled to.
        requestAnimationFrame(() => {
            const target = document.querySelector(
                `[data-blocked-field="${notice.field}"]`
            );
            if (!target) return;
            target.scrollIntoView({ behavior: "smooth", block: "center" });
            const focusable = target.querySelector(
                "input, textarea, [tabindex]:not([tabindex='-1'])"
            );
            focusable?.focus({ preventScroll: true });
        });
    }, []);

    // The form has its own URL, so the queue's hidden button is no longer the
    // gate -- see workOrderFormAccess. `types` is also what the type code is
    // resolved from, so a type the write would refuse is never raised.
    const access = useMemo(
        () =>
            workOrderFormAccess({
                workOrderTypes,
                workOrder,
                // Until the lookup has RESOLVED the id is undefined, which
                // closes the gate. A component that stops waiting is not
                // allowed to turn into an open door.
                currentUserID: userResolved ? currentUserID : undefined,
                hasPermission,
            }),
        [workOrderTypes, workOrder, currentUserID, userResolved, hasPermission]
    );

    // The field that is still sent, without the control that used to ask for
    // it. On an edit this is the type already on the record, which the service
    // refuses to let change.
    const workOrderTypeCode = useMemo(
        () => resolveWorkOrderTypeCode({ types: access.types, workOrder }),
        [access.types, workOrder]
    );

    // Resolved from the SAME list as the code above, so the heading and the
    // payload cannot name different types. Empty until the config lands, which
    // is what leaves the heading unsuffixed rather than suffixed with nothing.
    const workOrderTypeLabel = useMemo(
        () => resolveWorkOrderTypeLabel({ types: access.types, workOrder }),
        [access.types, workOrder]
    );

    useEffect(() => {
        onWorkOrderTypeLabelChange?.(workOrderTypeLabel);
    }, [workOrderTypeLabel, onWorkOrderTypeLabelChange]);

    // What every write and every check is built from: the box parsed into
    // serials, with the resolved type put back on.
    const resolvedForm = useMemo(
        () => ({
            ...resolveFormSerials(form, movementTypesByCode),
            workOrderTypeCode,
        }),
        [form, movementTypesByCode, workOrderTypeCode]
    );

    // The same parse the payload path runs, kept for the BAND to describe
    // itself: the live count, the per-line errors and the cap all read from
    // here. Parsing twice over fifty rows costs nothing and keeps the payload
    // transform free of anything the screen needs.
    const paste = useMemo(
        () =>
            parsePastedRows(
                form.movements[0]?.serialText,
                movementPasteSpec(
                    movementTypesByCode?.[form.movements[0]?.movementTypeCode] || null
                )
            ),
        [form, movementTypesByCode]
    );

    // Blocks BOTH Preview and Submit. resolveFormSerials keeps only the rows
    // that parsed, so without this a paste with three broken rows would
    // quietly submit the other forty-seven.
    const pasteError = useMemo(() => pasteValidationError(paste), [paste]);

    // The CODE as the form holds it, and the resolved config row. The auto
    // check waits on the code; everything else needs the row.
    const movementTypeCode = form.movements[0]?.movementTypeCode || "";
    const movementType = movementTypesByCode?.[movementTypeCode] || null;
    const singleColumns = useMemo(
        () => singleEntryColumns(movementType),
        [movementType]
    );

    const isSingle = form.movements[0]?.entryMode !== ENTRY_MODE.PASTE;

    // Single mode's own blocking check, per field.
    //
    // It is the SAME RULE the parser applies to a pasted row -- every column
    // required -- said per field, because in single mode there is no line 1.
    const singleErrors = useMemo(
        () =>
            isSingle
                ? singleEntryErrors(form.movements[0]?.singleValues, singleColumns)
                : {},
        [isSingle, form, singleColumns]
    );

    // Whichever mode is on, ONE message blocks Preview and Submit.
    const entryBlocked = isSingle
        ? Object.keys(singleErrors).length > 0
        : Boolean(pasteError);

    const applyEntryErrors = useCallback(
        (target) => {
            if (!isSingle) {
                if (pasteError) target["movements.0.serials"] = pasteError;
                return target;
            }
            Object.entries(singleErrors).forEach(([key, message]) => {
                target[`movements.0.single.${key}`] = message;
            });
            return target;
        },
        [isSingle, pasteError, singleErrors]
    );

    // useMemo, not a bare `|| []`: a fresh literal each render would change the
    // identity of the check state and of handleCheckSerials on every keystroke
    // anywhere else on the form.
    const serials = useMemo(
        () => resolvedForm.movements[0]?.serials || [],
        [resolvedForm]
    );

    // A STRING, so it can sit in a dependency array and be compared by value
    // the way the serial signature already is.
    const route = useMemo(
        () => routeSignature(resolvedForm.movements[0]),
        [resolvedForm]
    );

    const checkState = useMemo(
        () =>
            serialCheckState({
                serials,
                checkedSerials,
                route,
                checkedRoute,
                isChecking,
            }),
        [serials, checkedSerials, route, checkedRoute, isChecking]
    );

    // --- the dry run -------------------------------------------------------
    //
    // Same endpoint, same payload, same resolver, with validateOnly set: the
    // service stops after every check and before the first write and answers
    // 200 with the body the 422 carries. The check and the create therefore
    // cannot disagree, because they are one function.
    // WHAT IS KNOWABLE WITHOUT A REQUEST: the cap, the per-line parse errors,
    // and single mode's empty fields. None is worth a round trip to discover,
    // and on the AUTOMATIC path none is worth complaining about either -- a
    // half-typed form is not a mistake, it is a form being filled in.
    const checkBlockers = useCallback(() => {
        if (entryBlocked) return applyEntryErrors({});
        return validateSerialCheck(resolvedForm, movementTypesByCode, { dateBounds });
    }, [entryBlocked, applyEntryErrors, resolvedForm, movementTypesByCode, dateBounds]);

    // Guards against a slow earlier answer overwriting a faster later one.
    // With the check firing on its own this is no longer theoretical: editing
    // a destination while a request for the previous one is still out is now
    // an ordinary thing to do.
    const checkSeq = useRef(0);

    const runCheck = useCallback(async ({ announce }) => {
        const blockers = checkBlockers();
        if (Object.keys(blockers).length) {
            // ANNOUNCE only when the user asked. The automatic path stays
            // silent and simply waits for the form to become checkable --
            // reddening fields somebody has not reached yet is the behaviour
            // this form has already been corrected for once.
            if (announce) refuseAction(blockers);
            return;
        }
        if (announce) {
            setErrors({});
            setBlockedNotice(null);
        }
        const seq = ++checkSeq.current;
        setIsChecking(true);
        try {
            // The same payload a create posts. remarks is exempt from the
            // required rule on a dry run, so nothing is substituted.
            const res = await checkWorkOrderSerials(
                buildPreviewPayload(stampMovementDate(resolvedForm))
            );
            // The SHAPE, not the status code: a clean check is a 200 and a
            // dirty one is a 422, and both carry the same table.
            // Superseded by a later request: drop it whole. The winner holds
            // the highest seq and reaches the setIsChecking(false) below.
            if (seq !== checkSeq.current) return;

            const report = summariseSerialReport(res);
            if (report) {
                setSerialReport(report);
                setCheckedSerials(serials);
                setCheckedRoute(route);
                setCheckFailed(false);
                return;
            }
            // Anything else is an ordinary failure with an ordinary message,
            // and the PREVIOUS answer must not be left standing as though it
            // had just been confirmed.
            clearCheck();
            // REMEMBERED, so the automatic path does not immediately try
            // again and again against a server that is down. The form is
            // still stale, so without this the effect below would re-fire the
            // moment this one finished.
            setCheckFailed(true);
            // ONLY WHEN THE USER ASKED. A check nobody requested must not
            // throw a red toast over what they are typing -- the failure is
            // already visible as the "Try again" button this latch reveals.
            if (announce) {
                openNotification({
                    text: res?.responseStatus?.msg || "Could not check these serials",
                    icon: "toast-error",
                });
            }
        } finally {
            if (seq === checkSeq.current) setIsChecking(false);
        }
    }, [
        checkBlockers,
        resolvedForm,
        serials,
        route,
        clearCheck,
        stampMovementDate,
        openNotification,
        refuseAction,
    ]);

    // The button's version: says why when it cannot run.
    const handleCheckSerials = useCallback(
        () => runCheck({ announce: true }),
        [runCheck]
    );

    // THE REPORT KEEPS ITSELF UP TO DATE.
    //
    // It used to be a button. The argument for that was real -- a check is a
    // server round trip over every serial in the box, and firing it per
    // keystroke means a request per pause and partial answers while a paste is
    // still landing -- but the price was worse: the Route panel could not say
    // where the assets were until somebody pressed Preview, so the form sat
    // there holding an answer it already had the inputs for.
    //
    // WHAT IT DEPENDS ON is not guessed at here. serialCheckState already
    // compares the live serials and the live route against the ones the
    // standing answer was computed from -- that is what `isStale` means -- so
    // "the inputs changed" is a value this component already has. Anything
    // that moves the serials, the movement type, the destination type or the
    // destination entity makes the answer stale and re-runs it. Nothing else
    // can, because nothing else is in either signature.
    //
    // THE DEBOUNCE IS WHAT MAKES IT AFFORDABLE. One timer, reset by every
    // change, so a fifty-line paste is one request and typing a serial by hand
    // is one request after the typing stops -- not one per keystroke.
    // NOT ON AN EDIT'S FIRST PAINT. Opening somebody's pending request would
    // otherwise POST a dry run 600ms after load with no user action -- the
    // exemption shouldAutoCheckOnMount used to carry, lost when that helper
    // was replaced.
    //
    // GATED ON A TOUCH, not on the check's own state: "nothing checked yet and
    // nothing stale yet" is true forever on an edit nobody has checked, so an
    // edit would never check at all. updateMovement is the one funnel every
    // movement edit passes through, which makes it the honest signal for "the
    // maker has changed something".
    const editUntouched = isEdit && !movementTouched;

    // Nothing knowable-without-a-request is wrong. `autoCheckDue` did not ask
    // this, so a duplicate serial in a paste -- or an emptied field on an edit
    // -- left the caption promising "Rechecking…" forever: runCheck bails on a
    // blocker before it touches state, so nothing re-fires and no Try again
    // appears either.
    const checkable = Object.keys(checkBlockers()).length === 0;

    const autoCheckDue =
        checkable
        && !configLoading
        && !saving
        && !isChecking
        && !checkFailed
        && !editUntouched
        && serials.length > 0
        && Boolean(movementTypeCode)
        && (checkedSerials === null || checkState.isStale);

    useEffect(() => {
        if (!autoCheckDue) return undefined;
        const timer = setTimeout(
            () => runCheck({ announce: false }),
            AUTO_CHECK_DELAY_MS
        );
        return () => clearTimeout(timer);
    }, [autoCheckDue, runCheck]);

    // An input moved, so a previous transport failure is worth another try.
    //
    // KEYED ON THE SIGNATURE, not on `serials`. That comment used to claim a
    // signature and pass the array: `serials` is rebuilt whenever `form`
    // changes, so typing one character into REMARKS produced a new array with
    // identical contents and cleared the latch. Against a server that is down
    // that meant a fresh POST and a fresh error toast every 600ms while
    // somebody typed a sentence -- the exact loop the latch exists to stop.
    const serialsKey = useMemo(() => serialSignature(serials), [serials]);
    useEffect(() => {
        setCheckFailed(false);
    }, [route, serialsKey]);

    // What the serial report's "Remove these serials" button does.
    //
    // Rewrites the BOX, leaving everything else alone: the maker drops two
    // bad serials and submits again rather than re-entering fifty. The result
    // is dropped with them -- it described a list that no longer exists.
    const removeSerialsFromForm = useCallback((serialsToRemove) => {
        // A touch like any other -- dropping serials on an edit must let the
        // automatic check run again.
        setMovementTouched(true);
        setForm((prev) => ({
            ...prev,
            // PER MOVEMENT, because the column count is a property of the
            // movement TYPE: a comma separates serials in a one-column box and
            // cells in an intake row, and only the type says which.
            movements: prev.movements.map((m) => {
                const spec = movementPasteSpec(
                    movementTypesByCode?.[m.movementTypeCode]
                );
                const serialText = removeSerialsFromText(
                    m.serialText, serialsToRemove,
                    { columnCount: spec.columns.length }
                );
                return {
                    ...m,
                    serialText,
                    // BOTH HALVES, or single mode desyncs. The paste box reads
                    // serialText, but single mode renders from singleValues --
                    // patchSingle is otherwise the only thing that writes the
                    // two together. Rewriting only the text left the refused
                    // serial sitting in the Serial Number field the user had
                    // just been told to clear, and a submit that carried
                    // nothing.
                    singleValues: m.entryMode === ENTRY_MODE.PASTE
                        ? m.singleValues
                        : singleValuesFromParsed(parsePastedRows(serialText, spec)),
                };
            }),
        }));
        clearCheck();
    }, [clearCheck, movementTypesByCode]);

    // --- submit ------------------------------------------------------------
    // WHAT THE SUBMIT WILL RECORD, in the org's timezone and through the same
    // helper every other date on this page uses -- never new Date(), which is
    // the browser's zone and would show a support user's afternoon as the
    // fleet's. The value is stamped again at submit, so a form left open for an
    // hour does not record the hour it was opened in; this is what that stamp
    // currently says.
    const recordedAtLabel = useMemo(
        () => formatWorkOrderDateTime(movement?.movementDateTime, "now"),
        [movement]
    );

    // THE CHECK'S VERDICT IS A GATE, not advice.
    //
    // A serial the dry run has just refused will be refused again by the
    // create call, so offering Submit over a report that says "not allowed"
    // only buys the user a round trip to the same answer -- and when every
    // serial is refused there is nothing left for the request to carry.
    //
    // ONLY A CURRENT REPORT GATES. A stale one describes a box the user has
    // already edited, and a recheck is on its way; blocking on it would refuse
    // a form whose problems may be gone, and after a transport failure nothing
    // would ever clear it.
    const refusedByCheck = useMemo(() => {
        if (!serialReport || checkState?.isStale) return null;
        const problems = serialReport.problemSerials?.length || 0;
        if (!problems) return null;
        const total = serialReport.summary?.total || problems;
        return {
            message:
                problems >= total
                    ? `${problems} serial${problems === 1 ? "" : "s"} must be fixed first`
                    : `${problems} serial${problems === 1 ? "" : "s"} must be fixed or removed first`,
        };
    }, [serialReport, checkState]);

    const handleSubmit = async () => {
        // The button is disabled for this, so reaching here means a keyboard
        // or a race got past it. Saying why beats sending a request the server
        // will refuse for the same reason.
        if (refusedByCheck) {
            setBlockedNotice(refusedByCheck);
            return;
        }
        const localErrors = validateWorkOrderForm(resolvedForm, movementTypesByCode, {
            dateBounds,
        });
        // A row that did not parse is not a row that was left out -- the
        // resolved form silently drops it, so submitting past a parse error
        // would raise a request for the serials that happened to be readable.
        // Single mode's empty fields block here through the same call.
        applyEntryErrors(localErrors);
        if (Object.keys(localErrors).length) {
            // Submit fails the same way Preview does -- an empty date, a
            // missing destination -- so it says so in the same place.
            refuseAction(localErrors);
            return;
        }
        setErrors({});
        setBlockedNotice(null);
        setSaving(true);
        try {
            const payload = buildWorkOrderPayload(stampMovementDate(resolvedForm));
            const res = isEdit
                ? await updateWorkOrder(workOrder.id, payload)
                : await createWorkOrder(payload);

            if (
                res?.responseStatus?.code === CODE.SUCCESS ||
                res?.responseStatus?.code === CODE.URL_CREATED
            ) {
                setSerialReport(null);
                openNotification({
                    text:
                        res?.responseStatus?.msg ||
                        (isEdit ? "Work order updated" : "Work order created"),
                    icon: "toast-success",
                });
                onSaved?.(res?.data || null);
                return;
            }

            // THE STRUCTURED FAILURE. Detected by the SHAPE of the body, not
            // by the status code: create answers 422 and approve answers 409
            // with the same thing, and a check on the code alone misses one.
            const report = summariseSerialReport(res);
            if (report) {
                setSerialReport(report);
                // Submit re-resolves every serial server-side, so this answer
                // is as current as an answer gets -- it describes exactly the
                // list that was just sent.
                setCheckedSerials(serials);
                // No toast. The table IS the message, and a toast beside it
                // would be the thing the user reads and dismisses.
                return;
            }

            setSerialReport(null);
            openNotification({
                text: res?.responseStatus?.msg || "Could not save the work order",
                icon: "toast-error",
            });
        } finally {
            setSaving(false);
        }
    };

    // An edit waits for the identity as well as the config. Rendering the
    // denial while /api/rbac/me/permissions is in flight would flash "we could
    // not confirm who you are" at the maker of the very request being opened.
    if (configLoading || (isEdit && !userResolved)) {
        return (
            <Box
                data-testid="work-order-form-loading"
                sx={{ display: "flex", justifyContent: "center", py: 8 }}
            >
                <CircularProgress size={24} />
            </Box>
        );
    }

    // A config that never loaded is NOT a denial: with no types in hand the
    // gate cannot tell a view-only user from a failed request, and saying "you
    // do not have permission" to someone who simply lost the network is the
    // worse of the two mistakes.
    if (configError) {
        return (
            <Typography
                data-testid="work-order-config-error"
                sx={{ fontSize: 13, color: FORM_TOKENS.danger }}
            >
                {configError}
            </Typography>
        );
    }

    if (!access.allowed) {
        return (
            <Box
                data-testid="work-order-form-denied"
                sx={{
                    border: `1px solid ${FORM_TOKENS.cardBorder}`,
                    borderRadius: "10px",
                    bgcolor: FORM_TOKENS.surface,
                    p: 3,
                }}
            >
                <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.ink, mb: 0.5 }}>
                    {access.reason}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkFaint }}>
                    Work orders are raised by one person and reviewed by another, and the
                    right to raise one is granted per type.
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <CancelFormActionButton
                        data-testid="work-order-denied-back"
                        onClick={() => onCancel?.()}
                    >
                        Back to {backLabel}
                    </CancelFormActionButton>
                </Box>
            </Box>
        );
    }

    return (
        <Box data-testid="work-order-form">
            {/* TWO COLUMNS above lg, one below it.
                
                The form and its dry run used to be one column, so the answer
                to "can this actually be saved" sat below the fold of the
                thing it was answering: filling in the route scrolled the
                verdicts out of view, and reading the verdicts scrolled away
                the fields that caused them. Beside the form, both are on
                screen at once and the check reads as feedback rather than as
                a further section to complete.

                lg (1200px) is where that stops being true -- below it the two
                columns would each be too narrow for their own content, so the
                rail returns to the stack. The ACTIONS BAND is outside both
                columns on purpose: it commits the whole form, not the left
                half of it, and putting it last in the DOM is what makes the
                stacked order read form, then preview, then submit. */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    alignItems: "flex-start",
                    gap: 2,
                }}
            >
                <Box sx={{ flex: "1 1 0", minWidth: 0, width: "100%" }}>
                    <FormCard>
                        <MovementBuilder
                            movement={movement}
                            movementTypes={movementTypes}
                            movementTypesByCode={movementTypesByCode}
                            entityState={entityState}
                            dateBounds={dateBounds}
                            dateIsEditable={dateIsEditable}
                            onChange={updateMovement}
                            errors={errors}
                            disabled={saving}
                            serialReport={serialReport}
                            checkState={checkState}
                            paste={paste}
                            metaInfo={metaInfo}
                            onEntityCreated={addEntity}
                            onAutoFill={autoFillMovement}
                        />

                        {/* 4 -- why. LAST, and with no section header: a note about a
                            request belongs after the thing it is about. */}
                        <FormBand testId="work-order-remarks-band">
                            {/* REQUIRED, and marked as such. The reviewer has nothing
                                else to go on -- the movement says what, never why --
                                and the schema refuses a blank one, so the asterisk is
                                the truth rather than emphasis. */}
                            <FieldLabel label="Remarks" required />
                            <StyledTextField
                                fullWidth
                                multiline
                                minRows={2}
                                size="small"
                                disabled={saving}
                                error={Boolean(errors.remarks)}
                                placeholder="Why this is being raised — the reviewer reads this"
                                value={form.remarks}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, remarks: e.target.value }))
                                }
                                inputProps={{ "data-testid": "work-order-remarks" }}
                            />
                            <Helper
                                testId="work-order-remarks-helper"
                                text={errors.remarks || ""}
                                isError={Boolean(errors.remarks)}
                            />
                        </FormBand>

                        {/* A request that is incoherent as a WHOLE rather than at one
                            field. workOrderTypeCode lands here now that no control
                            asks for it -- an org with the tab but no raisable type is
                            a configuration fault, and it must say so somewhere. */}
                        {(errors.movements || errors.duplicateSerials || errors.workOrderTypeCode) && (
                            <FormBand testId="work-order-form-errors">
                                {[errors.workOrderTypeCode, errors.movements, errors.duplicateSerials]
                                    .filter(Boolean)
                                    .map((message) => (
                                        <FormHelperText
                                            key={message}
                                            sx={{ fontSize: "12px", color: FORM_TOKENS.danger, ml: 0 }}
                                        >
                                            {message}
                                        </FormHelperText>
                                    ))}
                            </FormBand>
                        )}
                    </FormCard>
                </Box>

                {/* The rail. STICKY, and clearing the fixed 48px toolbar, so the
                    verdicts stay in view while the route above them is edited --
                    which is the entire reason the panel moved out here.

                    No height cap and no scroll of its own: SerialReportTable
                    already caps its body and scrolls internally, and a second
                    scroller wrapped around the first is how a 50-serial report
                    turns into two nested bars neither of which moves the thing
                    the reader is looking at. If the panel does outgrow the
                    viewport it simply scrolls with the page. */}
                <Box
                    data-testid="work-order-preview-rail"
                    sx={{
                        width: { xs: "100%", lg: "440px" },
                        flexShrink: 0,
                        position: { xs: "static", lg: "sticky" },
                        top: { lg: "64px" },
                    }}
                >
                    <WorkOrderPreviewPanel
                        checkState={checkState}
                        serialReport={serialReport}
                        paste={paste}
                        onRetryCheck={checkFailed ? handleCheckSerials : null}
                        recheckComing={autoCheckDue || isChecking}
                        onRemoveSerials={removeSerialsFromForm}
                        disabled={saving}
                        actions={
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "stretch",
                                    gap: 1.25,
                                }}
                            >
                                {/* AT THE ACTION, not in the panel head.
                                    Submit is refused by the same conditions
                                    that refuse the check, and a button that
                                    looks enabled and does nothing is the thing
                                    this sentence exists to stop. */}
                                {/* NEVER gated on having pressed Check. The
                                    create call re-resolves every serial with
                                    the same function, so skipping the check
                                    costs correctness nothing -- only the early
                                    warning. */}
                                <PrimaryFormLoadingActionButton
                                    data-testid="work-order-submit"
                                    onClick={handleSubmit}
                                    loading={saving}
                                    disabled={saving || Boolean(refusedByCheck)}
                                    fullWidth
                                >
                                    {isEdit ? "Save Changes" : "Submit for review"}
                                </PrimaryFormLoadingActionButton>
                                {/* DIRECTLY UNDER THE BUTTON it explains. A
                                    disabled control with no reason beside it
                                    is the thing this line exists to stop. */}
                                {(refusedByCheck || blockedNotice) && (
                                    <Typography
                                        data-testid="work-order-submit-blocked"
                                        sx={{
                                            fontSize: "12px",
                                            color: FORM_TOKENS.danger,
                                            textAlign: "center",
                                        }}
                                    >
                                        {(refusedByCheck || blockedNotice).message}
                                    </Typography>
                                )}
                                {/* WHAT WILL BE RECORDED, beside the control
                                    that records it. The date is the clock's
                                    answer rather than a question, so it is a
                                    statement here and not a field.

                                    NOT ON AN EDIT, where the date IS a field
                                    -- the Movement band shows the picker, and
                                    repeating its value here would put the same
                                    date on screen twice again. */}
                                {!dateIsEditable && !refusedByCheck && (
                                <Typography
                                    data-testid="work-order-recorded-at"
                                    sx={{
                                        fontSize: "12px",
                                        color: FORM_TOKENS.inkFaint,
                                        textAlign: "center",
                                    }}
                                >
                                    recorded {recordedAtLabel}
                                </Typography>
                                )}
                                {/* BACK, not Cancel. Nothing has been created
                                    yet, so there is nothing to cancel -- the
                                    button leaves the page, and saying so is
                                    what stops it reading as "discard". */}
                                <CancelFormActionButton
                                    data-testid="work-order-cancel"
                                    onClick={() => onCancel?.()}
                                    disabled={saving}
                                    sx={{ alignSelf: "center" }}
                                >
                                    Back to {backLabel}
                                </CancelFormActionButton>
                            </Box>
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default WorkOrderForm;
